/**
 * New vs Returning Clients Chart Component
 *
 * Donut chart showing the breakdown between new and returning clients.
 * Uses Recharts PieChart with custom center label and legend.
 */

import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import styles from './NewVsReturningChart.module.scss';

// Chart colors matching the design
const CHART_COLORS = {
    newClients: '#5E8677', // Dark green
    returningClients: '#BDD2CA' // Light green
} as const;

// Data structure for the chart
interface ClientData {
    name: string;
    value: number;
    percentage: number;
    color: string;
}

// Data matching the design exactly
const clientsData: ClientData[] = [
    {
        name: 'New clients',
        value: 350,
        percentage: 60,
        color: CHART_COLORS.newClients
    },
    {
        name: 'Returning clients',
        value: 100,
        percentage: 40,
        color: CHART_COLORS.returningClients
    }
];

// Calculate total
const totalClients = clientsData.reduce((sum, item) => sum + item.value, 0);

// Custom center label component
const CenterLabel: React.FC = () => {
    return (
        <div className={styles.centerLabel}>
            <div className={styles.totalValue}>{totalClients}</div>
            <div className={styles.totalPercentage}>100%</div>
        </div>
    );
};

// Custom legend component
const CustomLegend: React.FC = () => {
    return (
        <div className={styles.legend}>
            {clientsData.map((item, index) => (
                <div
                    key={index}
                    className={styles.legendItem}
                >
                    <div
                        className={styles.legendDot}
                        style={{ backgroundColor: item.color }}
                    />
                    <span className={styles.legendLabel}>{item.name}</span>
                    <span className={styles.legendValue}>
                        {item.value} ({item.percentage}%)
                    </span>
                </div>
            ))}
        </div>
    );
};

export const NewVsReturningChart: React.FC = () => {
    return (
        <div className={styles.chartContainer}>
            {/* Chart Title */}
            <h3 className={styles.chartTitle}>New vs. Returning clients</h3>

            {/* Donut Chart */}
            <div className={styles.chartWrapper}>
                <ResponsiveContainer
                    width="100%"
                    height={260}
                >
                    <PieChart>
                        <Pie
                            data={clientsData}
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={100}
                            startAngle={90}
                            endAngle={450}
                            dataKey="value"
                            stroke="none"
                        >
                            {clientsData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.color}
                                />
                            ))}
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>

                {/* Center Label */}
                <CenterLabel />
            </div>

            {/* Custom Legend */}
            <CustomLegend />
        </div>
    );
};
